import { type DefaultRootState } from '@databases/redux';
import { type SimpleStore } from '@databases/redux-types';
import { type Database } from '@databases/types/database';
import { DatabaseCalculationType } from '@databases/types/database/view';
import { getStringRepresentation } from '@databases/utils/database/field-types';
import {
  getDatabaseEntries,
  getDatabaseFieldRequired,
  getDatabaseValue,
  getNumberRepresentation,
} from '@databases/utils/database/utils';

import { getUniqueIdentifiers, isEmpty } from './quickCalcUtils';

export const sum = (
  databaseContentId: string,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>
): number => {
  let sum = 0;
  for (const entry of entries) {
    const numericValue = Number(entry.numberRepresentation ?? entry.value);
    if (!isNaN(numericValue)) {
      sum += numericValue;
    }
  }
  return sum;
};

export const countTotal = (
  databaseContentId: string,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>
): number => {
  return entries.length;
};

export const count = (
  databaseContentId: string,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>,
  store: SimpleStore<DefaultRootState>
): number => {
  return entries
    .filter((entry) => !isEmpty(databaseContentId, fieldId, entry.id, store))
    .flatMap((entry) => getUniqueIdentifiers(databaseContentId, fieldId, entry.id, store))
    .map((identifier) => identifier.trim()).length;
};

export const countEmpty = (
  databaseContentId: string,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>,
  store: SimpleStore<DefaultRootState>
): number => {
  const emptyEntries = entries.filter((entry) =>
    isEmpty(databaseContentId, fieldId, entry.id, store)
  );
  return emptyEntries.length;
};

export const countUnique = (
  databaseContentId: string,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>,
  store: SimpleStore<DefaultRootState>
): number => {
  const uniqueIdentifiers = entries
    .filter((entry) => !isEmpty(databaseContentId, fieldId, entry.id, store))
    .flatMap((entry) =>
      getUniqueIdentifiers(databaseContentId, fieldId, entry.id, store)
    );

  return new Set(uniqueIdentifiers).size;
};

export const percentEmpty = (
  databaseContentId: string,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>,
  store: SimpleStore<DefaultRootState>
): number | null => {
  if (entries.length === 0) {
    return null;
  }

  const emptyEntries = entries.filter((entry) =>
    isEmpty(databaseContentId, fieldId, entry.id, store)
  );
  const percentage = (emptyEntries.length / entries.length) * 100;
  return parseFloat(percentage.toFixed(3));
};

export const percentNotEmpty = (
  databaseContentId: string,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>,
  store: SimpleStore<DefaultRootState>
): number | null => {
  if (entries.length === 0) {
    return null;
  }

  const nonEmptyEntries = entries.filter(
    (entry) => !isEmpty(databaseContentId, fieldId, entry.id, store)
  );
  const percentage = (nonEmptyEntries.length / entries.length) * 100;
  return parseFloat(percentage.toFixed(3));
};

export const average = (
  databaseContentId: string,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>,
  store: SimpleStore<DefaultRootState>
): number | null => {
  const nonEmptyEntries = entries.filter(
    (entry) => !isEmpty(databaseContentId, fieldId, entry.id, store)
  );
  if (nonEmptyEntries.length === 0) {
    return null;
  }
  const _sum = sum(databaseContentId, fieldId, entries);
  return _sum / nonEmptyEntries.length;
};

export const min = (
  databaseContentId: string,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>,
  store: SimpleStore<DefaultRootState>
): number | null => {
  const nonEmptyEntries = entries.filter(
    (entry) => !isEmpty(databaseContentId, fieldId, entry.id, store)
  );

  if (nonEmptyEntries.length === 0) {
    return null;
  }

  return Math.min(
    ...nonEmptyEntries.map((entry) => Number(entry.numberRepresentation ?? entry.value))
  );
};

export const max = (
  databaseContentId: string,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>,
  store: SimpleStore<DefaultRootState>
): number | null => {
  const nonEmptyEntries = entries.filter(
    (entry) => !isEmpty(databaseContentId, fieldId, entry.id, store)
  );

  if (nonEmptyEntries.length === 0) {
    return null;
  }
  return Math.max(
    ...nonEmptyEntries.map((entry) => Number(entry.numberRepresentation ?? entry.value))
  );
};

export const calculationMap = {
  [DatabaseCalculationType.SUM]: sum,
  [DatabaseCalculationType.COUNT_TOTAL]: countTotal,
  [DatabaseCalculationType.COUNT]: count,
  [DatabaseCalculationType.COUNT_EMPTY]: countEmpty,
  [DatabaseCalculationType.COUNT_UNIQUE]: countUnique,
  [DatabaseCalculationType.PERCENT_EMPTY]: percentEmpty,
  [DatabaseCalculationType.PERCENT_NOT_EMPTY]: percentNotEmpty,
  [DatabaseCalculationType.AVERAGE]: average,
  [DatabaseCalculationType.MIN]: min,
  [DatabaseCalculationType.MAX]: max,
};

type CalculationType = keyof typeof calculationMap;
export function getCalculationFunction<T extends CalculationType>(
  type: T
): (typeof calculationMap)[T] {
  return calculationMap[type];
}

export function getAllEntriesInField(
  database: Database,
  fieldId: string,
  state: DefaultRootState | undefined,
  recursionTracker = { recursionCount: 0 }
) {
  const field = getDatabaseFieldRequired(database, fieldId);
  const entries = getDatabaseEntries(database);

  return entries.map((entry) => {
    const value = getDatabaseValue(database, field, entry);

    const stringRepresentation = getStringRepresentation(
      { database, field, entry, value },
      state,
      { recursionCount: recursionTracker.recursionCount }
    );

    const numberRepresentation = getNumberRepresentation(
      database,
      field,
      entry,
      value,
      state
    );

    return {
      id: entry.id,
      stringRepresentation,
      numberRepresentation,
      ...value,
    };
  });
}
